import { useState, useEffect, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Loader2, CheckCircle2, XCircle, Mail, CalendarClock } from "lucide-react";
import Logo from "@/components/Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function JoinReturn() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking"); // checking | success | failed | error
  const [info, setInfo] = useState({});
  const tries = useRef(0);

  useEffect(() => {
    if (!sessionId) { setState("error"); return; }
    let stop = false;
    const poll = async () => {
      try {
        const r = await fetch(`${API}/onboarding/status/${sessionId}`);
        const d = await r.json();
        if (stop) return;
        setInfo(d);
        if (d.payment_status === "paid" || d.paid) { setState("success"); return; }
        if (d.status === "expired") { setState("failed"); return; }
        if (tries.current++ < 12) setTimeout(poll, 2500);
        else setState("failed");
      } catch {
        if (tries.current++ < 12) setTimeout(poll, 2500);
        else setState("error");
      }
    };
    poll();
    return () => { stop = true; };
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-[#FAFAFA] flex flex-col">
      <div className="grain" />
      <header className="px-6 lg:px-8 h-20 flex items-center max-w-4xl mx-auto w-full">
        <Link to="/" aria-label="MBM home"><Logo className="h-6 w-auto" /></Link>
      </header>
      <main className="flex-1 flex items-center justify-center px-6 pb-24">
        <div className="w-full max-w-md text-center" data-testid="join-return">
          {state === "checking" && (
            <>
              <Loader2 className="h-8 w-8 text-[#9fd3ff] animate-spin mx-auto" />
              <p className="mt-6 text-sm text-zinc-400 font-light" data-testid="join-return-checking">Confirming your payment…</p>
            </>
          )}
          {state === "success" && (
            <div data-testid="join-return-success">
              <CheckCircle2 className="h-12 w-12 text-[#7ee0a1] mx-auto" strokeWidth={1.5} />
              <h1 className="mt-5 font-heading text-3xl font-light">Welcome to MBM.</h1>
              <p className="mt-3 text-sm text-zinc-400 font-light">
                Your payment for <span className="text-white">{info.plan_name}</span> is confirmed and your account is ready.
              </p>
              <div className="mt-8 space-y-3 text-left">
                <div className="glass-card p-4 flex items-start gap-3">
                  <Mail className="h-5 w-5 text-[#9fd3ff] shrink-0" strokeWidth={1.75} />
                  <div className="text-sm text-zinc-300 font-light">Check your inbox — we've emailed a link to <span className="text-white">set your password</span> and log in.</div>
                </div>
                <div className="glass-card p-4 flex items-start gap-3">
                  <CalendarClock className="h-5 w-5 text-[#e6c67a] shrink-0" strokeWidth={1.75} />
                  <div className="text-sm text-zinc-300 font-light">Our team will be in touch shortly to arrange your onboarding session.</div>
                </div>
              </div>
              <Link to="/login" className="btn-solid mt-8 inline-block px-6 py-3 text-sm font-medium" data-testid="join-return-login">Go to sign in</Link>
            </div>
          )}
          {(state === "failed" || state === "error") && (
            <div data-testid="join-return-failed">
              <XCircle className="h-12 w-12 text-[#ff7a7a] mx-auto" strokeWidth={1.5} />
              <h1 className="mt-5 font-heading text-3xl font-light">Payment not completed.</h1>
              <p className="mt-3 text-sm text-zinc-400 font-light">
                We couldn't confirm your payment. If you were charged, please contact support@mindbeforemarkets.com — otherwise you can try again.
              </p>
              <Link to="/#pricing" className="btn-glass text-white mt-8 inline-block px-6 py-3 text-sm font-medium">Back to programs</Link>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
